import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";

const KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const INITIAL = { wallet_add: "", accountType: "trc_20", password: "", error_list: {} };
const NETWORKS = [
  { value: "trc_20", name: "USDT TRC-20", network: "TRON Network", icon: "fa-solid fa-link" },
  { value: "bep_20", name: "USDT BEP-20", network: "BNB Smart Chain", icon: "fa-solid fa-network-wired" },
];
const errorText = (value) => Array.isArray(value) ? value.join(" ") : value ? String(value) : "";

const Checkout = () => {
  const [form, setForm] = useState(INITIAL);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [copyId, setCopyId] = useState(null);
  const mounted = useRef(true);
  const busy = useRef(false);

  const fetchIncomeData = useCallback(async () => {
    if (!localStorage.getItem("auth_token")) return;
    try {
      const response = await axios.post("/get-method", {
        key_s: KEY,
        currency: localStorage.getItem("selectedCurrency"),
      });
      if (mounted.current) {
        setAccounts(Array.isArray(response?.data?.data) ? response.data.data : []);
        setFetchError("");
      }
    } catch (error) {
      if (mounted.current) setFetchError("Unable to retrieve saved wallets. Please try again.");
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchIncomeData();
    return () => { mounted.current = false; };
  }, [fetchIncomeData]);

  const handleInput = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]: value,
      error_list: { ...previous.error_list, [name]: "" },
    }));
  };

  const depositSubmit = async (event) => {
    event.preventDefault();
    if (busy.current) return;
    if (!localStorage.getItem("auth_token")) {
      swal("Session Expired", "Please sign in to save a withdrawal method.", "warning");
      return;
    }
    const errors = {};
    if (!form.wallet_add.trim()) errors.wallet_add = "Wallet address is required.";
    if (!form.password) errors.password = "Account password is required.";
    if (!NETWORKS.some((network) => network.value === form.accountType)) errors.accountType = "Select a valid network.";
    if (Object.keys(errors).length) {
      setForm((previous) => ({ ...previous, error_list: errors }));
      return;
    }
    busy.current = true;
    setSaving(true);
    try {
      const response = await axios.post("/sub-method", {
        wallet_add: form.wallet_add.trim(),
        accountType: form.accountType,
        password: form.password,
        key_s: KEY,
      });
      const result = response.data || {};
      if (result.status === "success") {
        swal("Success", result.message || "Withdrawal method saved successfully.", "success");
        if (mounted.current) setForm({ ...INITIAL });
        await fetchIncomeData();
      } else if (result.status === "fail") {
        swal("Warning", result.message || "Unable to save this wallet.", "warning");
      } else if (result.validation_error) {
        if (mounted.current) setForm((previous) => ({ ...previous, error_list: result.validation_error }));
      } else {
        swal("Warning", result.message || "Unable to save this wallet.", "warning");
      }
    } catch (error) {
      const validation = error?.response?.data?.validation_error || error?.response?.data?.errors;
      if (validation && mounted.current) setForm((previous) => ({ ...previous, error_list: validation }));
      else swal("Connection Error", "Could not save the withdrawal method. Please try again.", "error");
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const copyAddress = async (address, id) => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(String(address));
      setCopyId(id);
    } catch (_) {
      swal("Copy Address", String(address), "info");
    }
  };

  return (
    <>
      <style>{`
        html,body,#root{background:#00191D!important;}
        .rcwm,.rcwm *{box-sizing:border-box}
        .rcwm{--navy:#00191D;--cyan:#16B8C7;--cyan2:#55dce3;--line:rgba(22,184,199,.22);--muted:#a7c1c5;min-height:100vh;background:#00191D;color:#fff;font-family:inherit;font-size:14px;padding:18px 4px 32px;line-height:1.5}
        .rcwm button,.rcwm input{font:inherit}
        .rcwm button{cursor:pointer}
        .rcwm-shell{width:100%;max-width:1220px;margin:0 auto}
        .rcwm-top{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:20px 22px;margin-bottom:16px;border:1px solid var(--line);border-radius:20px;background:linear-gradient(120deg,#052c32,#00191D 70%);animation:rcwm-in .35s ease both}
        .rcwm-eyebrow{color:var(--cyan2);font-size:11px;font-weight:800;letter-spacing:1.6px;text-transform:uppercase;margin:0 0 4px}
        .rcwm-title{margin:0;font-size:24px;font-weight:800;letter-spacing:-.6px;color:#fff}
        .rcwm-sub{color:var(--muted);margin:6px 0 0;font-size:13px}
        .rcwm-head-icon{width:52px;height:52px;flex-shrink:0;display:grid;place-items:center;border-radius:15px;color:#00191D;font-size:21px;background:linear-gradient(135deg,#55dce3,#16B8C7);box-shadow:0 8px 22px rgba(22,184,199,.16)}
        .rcwm-layout{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:16px;align-items:start}
        .rcwm-form-card{background:#fff;color:#122e33;border-radius:20px;padding:24px;border:1px solid #e0ebed;box-shadow:0 14px 40px rgba(0,0,0,.12);animation:rcwm-in .4s ease both}
        .rcwm-list-card{background:#05262c;color:#fff;border-radius:20px;padding:24px;border:1px solid var(--line);animation:rcwm-in .45s ease both}
        .rcwm-card-title{display:flex;align-items:center;gap:10px;font-size:17px;font-weight:800;margin:0 0 6px}
        .rcwm-card-title i{color:#0a9eaf}
        .rcwm-list-card .rcwm-card-title i{color:var(--cyan2)}
        .rcwm-card-sub{margin:0 0 22px;font-size:13px;color:#688087}
        .rcwm-list-card .rcwm-card-sub{color:var(--muted)}
        .rcwm-label{display:block;font-weight:750;font-size:13px;margin-bottom:8px;color:#193e44}
        .rcwm-field{margin-bottom:20px}
        .rcwm-input-wrap{position:relative}
        .rcwm-input{display:block;width:100%;height:49px;border-radius:12px;border:1px solid #d6e3e5;background:#f7fafb;color:#12353a;padding:0 14px;outline:0;transition:border-color .18s,box-shadow .18s;font-size:14px}
        .rcwm-input:focus{border-color:#16B8C7;box-shadow:0 0 0 3px rgba(22,184,199,.12)}
        .rcwm-input::placeholder{color:#81979b}
        .rcwm-password{padding-right:48px}
        .rcwm-visibility{position:absolute;right:7px;top:6px;height:37px;width:37px;border:0;background:transparent;color:#56747a;border-radius:8px}
        .rcwm-visibility:hover{background:#e6f4f5;color:#067f8d}
        .rcwm-error{display:block;color:#c52f47;font-size:12px;margin-top:6px}
        .rcwm-networks{display:grid;grid-template-columns:1fr 1fr;gap:10px}
        .rcwm-network{position:relative;display:block;cursor:pointer}
        .rcwm-network input{position:absolute;opacity:0;width:1px;height:1px}
        .rcwm-network-body{display:flex;align-items:center;gap:11px;border:1px solid #d7e5e7;border-radius:13px;padding:13px 11px;min-height:78px;transition:border-color .18s,background .18s,transform .18s}
        .rcwm-network:hover .rcwm-network-body{border-color:#16B8C7;transform:translateY(-2px)}
        .rcwm-network input:checked+.rcwm-network-body{background:#eafafb;border-color:#16B8C7;box-shadow:inset 0 0 0 1px #16B8C7}
        .rcwm-network input:focus-visible+.rcwm-network-body{outline:3px solid rgba(22,184,199,.3);outline-offset:2px}
        .rcwm-network-icon{width:37px;height:37px;flex:0 0 37px;display:grid;place-items:center;border-radius:11px;background:#e8f5f6;color:#0798a9}
        .rcwm-network strong{display:block;font-size:13px;color:#183c42}
        .rcwm-network small{display:block;font-size:11px;color:#70898e;margin-top:2px}
        .rcwm-submit{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;height:50px;border:0;border-radius:12px;background:linear-gradient(105deg,#0da7b7,#16B8C7,#52d6df);color:#00292f;font-weight:850;font-size:14px;transition:transform .18s,filter .18s;box-shadow:0 8px 22px rgba(22,184,199,.19)}
        .rcwm-submit:hover:not(:disabled){filter:brightness(1.05);transform:translateY(-2px)}
        .rcwm-submit:disabled{opacity:.65;cursor:not-allowed}
        .rcwm-note{display:flex;align-items:flex-start;gap:9px;margin-top:16px;padding:12px;border-radius:11px;background:#f1f8f9;color:#59777d;font-size:12px}
        .rcwm-note i{color:#0c9eac;margin-top:2px}
        .rcwm-list-top{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:14px}
        .rcwm-count{flex-shrink:0;padding:5px 10px;background:rgba(22,184,199,.13);border:1px solid var(--line);border-radius:999px;color:#70e3e9;font-size:12px;font-weight:800}
        .rcwm-wallets{display:grid;gap:11px}
        .rcwm-wallet{padding:15px;border-radius:14px;background:#092f35;border:1px solid rgba(22,184,199,.19);transition:border-color .18s,transform .18s}
        .rcwm-wallet:hover{border-color:rgba(22,184,199,.5);transform:translateY(-2px)}
        .rcwm-wallet-top{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:12px}
        .rcwm-chain{display:inline-flex;align-items:center;gap:7px;border-radius:8px;background:rgba(22,184,199,.14);padding:6px 9px;color:#6ae2e9;font-weight:800;font-size:12px}
        .rcwm-wallet-date{font-size:12px;color:var(--muted);text-align:right}
        .rcwm-address-label{display:block;color:var(--muted);font-size:11px;margin-bottom:5px}
        .rcwm-address-row{display:flex;align-items:center;gap:8px}
        .rcwm-address{min-width:0;flex:1;overflow-wrap:anywhere;color:#fff;font-size:13px;font-family:ui-monospace,SFMono-Regular,Consolas,monospace}
        .rcwm-copy{width:34px;height:34px;flex:0 0 34px;display:grid;place-items:center;border:1px solid rgba(22,184,199,.4);border-radius:9px;background:#0b3b42;color:#70e3e9;transition:background .18s}
        .rcwm-copy:hover{background:#15505a}
        .rcwm-empty{padding:42px 18px;border:1px dashed rgba(22,184,199,.3);border-radius:14px;text-align:center;color:var(--muted)}
        .rcwm-empty i{display:block;font-size:26px;color:#61d8df;margin-bottom:12px}
        .rcwm-alert{padding:11px 12px;margin-bottom:12px;border:1px solid rgba(255,137,137,.4);border-radius:11px;color:#ffb2b2;font-size:12px}
        .rcwm-retry{border:1px solid rgba(22,184,199,.4);border-radius:9px;background:#0b3b42;color:#b8f6f8;padding:8px 12px;margin-top:8px}
        .rcwm-footer-note{display:flex;gap:9px;align-items:flex-start;margin-top:16px;padding:12px;border:1px solid var(--line);border-radius:11px;color:var(--muted);font-size:12px}
        .rcwm-footer-note i{color:#62dce4;margin-top:2px}
        @keyframes rcwm-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @media(max-width:850px){.rcwm-layout{grid-template-columns:1fr}.rcwm-form-card,.rcwm-list-card{padding:20px}}
        @media(max-width:480px){.rcwm{padding:10px 4px 24px}.rcwm-top{padding:16px;border-radius:16px}.rcwm-title{font-size:20px}.rcwm-head-icon{width:44px;height:44px;font-size:18px}.rcwm-form-card,.rcwm-list-card{padding:16px;border-radius:16px}.rcwm-networks{gap:8px}.rcwm-network-body{padding:10px 8px;gap:7px}.rcwm-network-icon{width:30px;height:30px;flex-basis:30px}.rcwm-network strong{font-size:12px}.rcwm-network small{font-size:10px}.rcwm-wallet-top{align-items:flex-start}}
        @media(prefers-reduced-motion:reduce){.rcwm *{animation:none!important;transition:none!important}}
      `}</style>
      <main className="rcwm">
        <div className="rcwm-shell">
          <header className="rcwm-top">
            <div>
              <p className="rcwm-eyebrow">RM CROWN MARKET / ACCOUNT SETTINGS</p>
              <h1 className="rcwm-title">Withdrawal Methods</h1>
              <p className="rcwm-sub">Manage your cryptocurrency withdrawal destinations.</p>
            </div>
            <div className="rcwm-head-icon" aria-hidden="true"><i className="fa-solid fa-wallet" /></div>
          </header>

          <div className="rcwm-layout">
            <section className="rcwm-form-card" aria-labelledby="rcwm-setup-title">
              <h2 className="rcwm-card-title" id="rcwm-setup-title"><i className="fa-solid fa-circle-plus" /> Add Withdrawal Wallet</h2>
              <p className="rcwm-card-sub">Register a wallet address for your future withdrawals.</p>
              <form onSubmit={depositSubmit} noValidate>
                <div className="rcwm-field">
                  <label htmlFor="rcwm-address" className="rcwm-label">Crypto Wallet Address</label>
                  <input id="rcwm-address" name="wallet_add" type="text" className="rcwm-input" autoComplete="off" spellCheck={false} placeholder="Enter your USDT wallet address" value={form.wallet_add} onChange={handleInput} aria-invalid={Boolean(form.error_list.wallet_add)} />
                  {form.error_list.wallet_add && <small className="rcwm-error">{errorText(form.error_list.wallet_add)}</small>}
                </div>
                <div className="rcwm-field">
                  <span className="rcwm-label">Select Wallet Network</span>
                  <div className="rcwm-networks">
                    {NETWORKS.map((network) => (
                      <label className="rcwm-network" key={network.value}>
                        <input type="radio" name="accountType" value={network.value} checked={form.accountType === network.value} onChange={handleInput} />
                        <span className="rcwm-network-body">
                          <span className="rcwm-network-icon"><i className={network.icon} /></span>
                          <span><strong>{network.name}</strong><small>{network.network}</small></span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {form.error_list.accountType && <small className="rcwm-error">{errorText(form.error_list.accountType)}</small>}
                </div>
                <div className="rcwm-field">
                  <label htmlFor="rcwm-password" className="rcwm-label">Account Password</label>
                  <div className="rcwm-input-wrap">
                    <input id="rcwm-password" name="password" type={showPassword ? "text" : "password"} className="rcwm-input rcwm-password" autoComplete="current-password" placeholder="Confirm with your account password" value={form.password} onChange={handleInput} aria-invalid={Boolean(form.error_list.password)} />
                    <button type="button" className="rcwm-visibility" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}><i className={showPassword ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"} /></button>
                  </div>
                  {form.error_list.password && <small className="rcwm-error">{errorText(form.error_list.password)}</small>}
                </div>
                <button type="submit" className="rcwm-submit" disabled={saving}><i className="fa-solid fa-shield-halved" />{saving ? "Saving Wallet..." : "Save Withdrawal Method"}<i className="fa-solid fa-arrow-right" /></button>
              </form>
              <div className="rcwm-note"><i className="fa-solid fa-circle-info" /><span>Confirm that your address matches the selected network. Transfers to an incorrect network may be unrecoverable.</span></div>
            </section>

            <section className="rcwm-list-card" aria-labelledby="rcwm-list-title">
              <div className="rcwm-list-top">
                <div>
                  <h2 className="rcwm-card-title" style={{ color: "#ffffff" }}  id="rcwm-list-title"><i className="fa-solid fa-layer-group" /> Saved Withdrawal Accounts</h2>
                  <p className="rcwm-card-sub">Your registered withdrawal wallet details.</p>
                </div>
                <span className="rcwm-count">{accounts.length} {accounts.length === 1 ? "Wallet" : "Wallets"}</span>
              </div>
              {fetchError && <div className="rcwm-alert" role="alert">{fetchError}<div><button className="rcwm-retry" type="button" onClick={fetchIncomeData}>Try Again</button></div></div>}
              {accounts.length ? (
                <div className="rcwm-wallets">
                  {accounts.map((account, index) => (
                    <article className="rcwm-wallet" key={account.id ?? index}>
                      <div className="rcwm-wallet-top">
                        <span className="rcwm-chain"><i className="fa-solid fa-network-wired" /> {account.wallet_chain || "Network"}</span>
                        <span className="rcwm-wallet-date">{account.create_time || ""}</span>
                      </div>
                      <span className="rcwm-address-label">WALLET ADDRESS</span>
                      <div className="rcwm-address-row">
                        <span className="rcwm-address" title={account.wallet_address || ""}>{account.wallet_address || "—"}</span>
                        <button className="rcwm-copy" type="button" onClick={() => copyAddress(account.wallet_address, account.id ?? index)} title="Copy wallet address" aria-label="Copy wallet address"><i className={copyId === (account.id ?? index) ? "fa-solid fa-check" : "fa-regular fa-copy"} /></button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : !fetchError ? (
                <div className="rcwm-empty"><i className="fa-regular fa-folder-open" />No withdrawal wallets saved yet.<br />Your registered accounts will appear here.</div>
              ) : null}
              <div className="rcwm-footer-note"><i className="fa-solid fa-lock" /><span>Wallet changes are submitted using your account password and processed by the existing account API.</span></div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default Checkout;
